import {
  Badge,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Link,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import SlackHashLogo from "@/common/assets/slack-hash-logo.svg?react";
import AutocompleteSelect from "@/common/components/Autocomplete/AutocompleteSelect";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import { RuleSimpleSelection } from "@/common/components/Rule/RuleSimpleSelectionProps";
import SimpleSelect from "@/common/components/SimpleSelect";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import api from "@/common/utils/api";
import { normalizeSlackChannel } from "@/common/utils/slack";
import { EnvironmentDisplayName } from "@/environment/components/EnvironmentDisplayName";
import { LinearConnectionIcon } from "@/integration/components/linear/LinearConnectionIcon";
import { SlackChannelAutocompleteSelect } from "@/integration/components/slack/SlackChannelAutocompleteSelect";
import { integrationQueryKeys } from "@/integration/data/integrationQueryKeys";
import { SlackChannel } from "@reflag/shared/slackConnectionAPI";
import {
  defaultTargetingNotificationPolicyForProvider,
  TargetingNotificationPolicy,
} from "@reflag/shared/targetingNotificationPolicy";
import {
  LinearNotificationSettings,
  PatchLinearNotificationSettings,
  PatchSlackNotificationSettings,
  SlackNotificationSettings,
} from "@reflag/shared/targetingNotificationPolicyAPI";
import { GlobalSettingsUrl } from "@reflag/shared/urls";

const notificationSettingsQueryOptions = {
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: false,
};

const policyOptions: {
  label: string;
  value: TargetingNotificationPolicy;
}[] = [
  { label: "All changes", value: "ALL_CHANGES" },
  { label: "Stage updates only", value: "STAGE_UPDATES_ONLY" },
  { label: "Off", value: "DISABLED" },
];

type EnvironmentOption = {
  id: string;
  name: string;
  isProduction: boolean;
};

type EnvironmentSelectorProps = {
  environments: EnvironmentOption[];
  selectedEnvironmentIds: string[];
  isDisabled?: boolean;
  addLabel: string;
  onChange: (environmentIds: string[]) => void;
};

function EnvironmentSelector({
  environments,
  selectedEnvironmentIds,
  isDisabled,
  addLabel,
  onChange,
}: EnvironmentSelectorProps) {
  const environmentById = useMemo(
    () =>
      new Map(environments.map((environment) => [environment.id, environment])),
    [environments],
  );

  const selectedEnvironments = useMemo(
    () =>
      selectedEnvironmentIds
        .map((environmentId) => environmentById.get(environmentId))
        .filter(
          (environment): environment is EnvironmentOption => !!environment,
        ),
    [environmentById, selectedEnvironmentIds],
  );

  const availableEnvironments = useMemo(
    () =>
      environments.filter(
        (environment) => !selectedEnvironmentIds.includes(environment.id),
      ),
    [environments, selectedEnvironmentIds],
  );

  return (
    <VStack align="flex-start" spacing={2} w="full">
      <Flex flexWrap="wrap" gap={2} w="full">
        {selectedEnvironments.map((environment) => (
          <RuleSimpleSelection
            key={environment.id}
            readOnly={isDisabled}
            onRemove={() =>
              onChange(
                selectedEnvironmentIds.filter(
                  (environmentId) => environmentId !== environment.id,
                ),
              )
            }
          >
            <EnvironmentDisplayName environment={environment} fontSize="sm" />
          </RuleSimpleSelection>
        ))}
      </Flex>

      <AutocompleteSelect<EnvironmentOption>
        isDisabled={isDisabled || availableEnvironments.length === 0}
        itemKeyProperty="id"
        itemRenderFn={(environment) => (
          <EnvironmentDisplayName environment={environment} fontSize="sm" />
        )}
        maxW="sm"
        placeholder={
          availableEnvironments.length === 0
            ? "All environments added"
            : addLabel
        }
        suggestions={availableEnvironments}
        value={null}
        onChange={(environment) => {
          if (!environment) {
            return;
          }

          onChange([...selectedEnvironmentIds, environment.id]);
        }}
      />
    </VStack>
  );
}

function channelsMatch(a: SlackChannel | null, b: SlackChannel | null) {
  return (a?.id ?? null) === (b?.id ?? null);
}

function buildSavedEnabledMap(
  environments: EnvironmentOption[],
  enabledByEnvironmentId: Map<string, boolean>,
) {
  const next: Record<string, boolean> = {};
  for (const environment of environments) {
    next[environment.id] =
      enabledByEnvironmentId.get(environment.id) ?? environment.isProduction;
  }
  return next;
}

function getSelectedEnvironmentIds(
  enabledByEnvironmentId: Record<string, boolean>,
  environments: EnvironmentOption[],
) {
  return environments
    .filter((environment) => enabledByEnvironmentId[environment.id])
    .map((environment) => environment.id);
}

function toEnabledByEnvironmentId(
  selectedEnvironmentIds: string[],
  environments: EnvironmentOption[],
) {
  const selectedEnvironmentIdSet = new Set(selectedEnvironmentIds);

  return Object.fromEntries(
    environments.map((environment) => [
      environment.id,
      selectedEnvironmentIdSet.has(environment.id),
    ]),
  ) as Record<string, boolean>;
}

export default function Notifications() {
  const { app, env, org } = useEnvironment();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isSavingSlack, setIsSavingSlack] = useState(false);
  const [isSavingLinear, setIsSavingLinear] = useState(false);

  const environments = useMemo(
    () => [...app.environments].sort((a, b) => a.order - b.order),
    [app.environments],
  );
  const slackNotificationSettingsQueryKey =
    integrationQueryKeys.appSlackNotificationSettings(app.id);
  const linearNotificationSettingsQueryKey =
    integrationQueryKeys.appLinearNotificationSettings(app.id);

  const slackNotificationSettingsQuery = useQuery({
    queryKey: slackNotificationSettingsQueryKey,
    queryFn: () =>
      api
        .get<"/apps/:appId/notification-settings/slack">(
          `/apps/${app.id}/notification-settings/slack`,
        )
        .then((res) => res.data),
    ...notificationSettingsQueryOptions,
  });

  const linearNotificationSettingsQuery = useQuery({
    queryKey: linearNotificationSettingsQueryKey,
    queryFn: () =>
      api
        .get<"/apps/:appId/notification-settings/linear">(
          `/apps/${app.id}/notification-settings/linear`,
        )
        .then((res) => res.data),
    ...notificationSettingsQueryOptions,
  });

  const savedSlackChannel = useMemo(() => {
    if (slackNotificationSettingsQuery.data !== undefined) {
      return normalizeSlackChannel(
        slackNotificationSettingsQuery.data.slackChannel,
      );
    }

    return normalizeSlackChannel(app.integrations?.slack?.channel);
  }, [slackNotificationSettingsQuery.data, app.integrations?.slack?.channel]);

  const [slackChannel, setSlackChannel] = useState<SlackChannel | null>(
    savedSlackChannel ?? null,
  );
  const [slackPolicy, setSlackPolicy] = useState<TargetingNotificationPolicy>(
    defaultTargetingNotificationPolicyForProvider("slack"),
  );
  const [linearPolicy, setLinearPolicy] = useState<TargetingNotificationPolicy>(
    defaultTargetingNotificationPolicyForProvider("linearComment"),
  );
  const [slackSelectedEnvironmentIds, setSlackSelectedEnvironmentIds] =
    useState<string[]>([]);
  const [linearSelectedEnvironmentIds, setLinearSelectedEnvironmentIds] =
    useState<string[]>([]);

  const savedSlackPolicy =
    slackNotificationSettingsQuery.data?.policy ??
    defaultTargetingNotificationPolicyForProvider("slack");
  const savedLinearPolicy =
    linearNotificationSettingsQuery.data?.policy ??
    defaultTargetingNotificationPolicyForProvider("linearComment");

  const savedSlackEnabledByEnvironment = useMemo(() => {
    const enabledByEnvironmentId = new Map(
      (slackNotificationSettingsQuery.data?.environments ?? []).map(
        (environment) => [environment.environmentId, environment.enabled],
      ),
    );

    return buildSavedEnabledMap(environments, enabledByEnvironmentId);
  }, [environments, slackNotificationSettingsQuery.data?.environments]);

  const savedLinearEnabledByEnvironment = useMemo(() => {
    const enabledByEnvironmentId = new Map(
      (linearNotificationSettingsQuery.data?.environments ?? []).map(
        (environment) => [environment.environmentId, environment.enabled],
      ),
    );

    return buildSavedEnabledMap(environments, enabledByEnvironmentId);
  }, [environments, linearNotificationSettingsQuery.data?.environments]);

  const isPoliciesLoading =
    slackNotificationSettingsQuery.isLoading ||
    linearNotificationSettingsQuery.isLoading;
  const hasPoliciesError =
    slackNotificationSettingsQuery.isError ||
    linearNotificationSettingsQuery.isError;

  useEffect(() => {
    setSlackChannel(savedSlackChannel ?? null);
  }, [savedSlackChannel]);

  useEffect(() => {
    setSlackPolicy(savedSlackPolicy);
  }, [savedSlackPolicy]);

  useEffect(() => {
    setLinearPolicy(savedLinearPolicy);
  }, [savedLinearPolicy]);

  useEffect(() => {
    setSlackSelectedEnvironmentIds(
      getSelectedEnvironmentIds(savedSlackEnabledByEnvironment, environments),
    );
  }, [environments, savedSlackEnabledByEnvironment]);

  useEffect(() => {
    setLinearSelectedEnvironmentIds(
      getSelectedEnvironmentIds(savedLinearEnabledByEnvironment, environments),
    );
  }, [environments, savedLinearEnabledByEnvironment]);

  const setAndRefreshNotificationSettings = async <TData,>(
    queryKey: readonly unknown[],
    data: TData,
  ) => {
    queryClient.setQueryData<TData>(queryKey, data);
    await queryClient.invalidateQueries({ queryKey });
  };

  const patchSlackNotificationSettings = async (
    payload: PatchSlackNotificationSettings,
  ) => {
    const { data } =
      await api.patch<"/apps/:appId/notification-settings/slack">(
        `/apps/${app.id}/notification-settings/slack`,
        payload,
      );

    await setAndRefreshNotificationSettings<SlackNotificationSettings>(
      slackNotificationSettingsQueryKey,
      data,
    );
  };

  const patchLinearNotificationSettings = async (
    payload: PatchLinearNotificationSettings,
  ) => {
    const { data } =
      await api.patch<"/apps/:appId/notification-settings/linear">(
        `/apps/${app.id}/notification-settings/linear`,
        payload,
      );

    await setAndRefreshNotificationSettings<LinearNotificationSettings>(
      linearNotificationSettingsQueryKey,
      data,
    );
  };

  const handleSaveSlack = async () => {
    const shouldUpdateChannel = !channelsMatch(
      slackChannel,
      savedSlackChannel ?? null,
    );

    setIsSavingSlack(true);
    try {
      const slackEnabledByEnvironment = toEnabledByEnvironmentId(
        slackSelectedEnvironmentIds,
        environments,
      );

      const payload: PatchSlackNotificationSettings = {
        slackChannel,
        policy: slackPolicy,
        environments: environments.map((environment) => ({
          environmentId: environment.id,
          enabled: slackEnabledByEnvironment[environment.id],
        })),
      };

      await patchSlackNotificationSettings(payload);

      if (shouldUpdateChannel) {
        await queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(org.id),
        });
      }

      toast({
        title: "Slack notification settings saved",
        status: "success",
      });
    } catch {
      toast({
        title: "Failed to save Slack notification settings",
        status: "error",
      });
    } finally {
      setIsSavingSlack(false);
    }
  };

  const handleSaveLinear = async () => {
    setIsSavingLinear(true);
    try {
      const linearEnabledByEnvironment = toEnabledByEnvironmentId(
        linearSelectedEnvironmentIds,
        environments,
      );

      const payload: PatchLinearNotificationSettings = {
        policy: linearPolicy,
        environments: environments.map((environment) => ({
          environmentId: environment.id,
          enabled: linearEnabledByEnvironment[environment.id],
        })),
      };

      await patchLinearNotificationSettings(payload);

      toast({
        title: "Linear notification settings saved",
        status: "success",
      });
    } catch {
      toast({
        title: "Failed to save Linear notification settings",
        status: "error",
      });
    } finally {
      setIsSavingLinear(false);
    }
  };

  return (
    <Flex direction="column" gap={4} maxW="wideForm">
      <Restricted alertStyle="callout" role="admin">
        <VStack align="flex-start" spacing={8} w="full">
          <VStack align="flex-start" spacing={2} w="full">
            <Heading size="md">Notification settings</Heading>
            <Text color="dimmed" fontSize="sm" maxW="2xl">
              Configure notification channels and targeting update defaults via
              Slack and Linear.
            </Text>
          </VStack>

          {isPoliciesLoading ? (
            <Flex justify="center" py={8} w="full">
              <DelayedSpinner />
            </Flex>
          ) : (
            <VStack align="flex-start" spacing={6} w="full">
              <Card w="full">
                <CardBody w="full">
                  <VStack align="flex-start" spacing={5} w="full">
                    <Flex align="center" justify="space-between" w="full">
                      <HStack>
                        <SlackHashLogo height="16" width="16" />
                        <Heading fontWeight="medium" size="sm">
                          Slack
                        </Heading>
                      </HStack>
                      <HStack spacing={3}>
                        <Badge
                          colorScheme={
                            org.integrations.slack ? "green" : "gray"
                          }
                        >
                          {org.integrations.slack
                            ? "Connected"
                            : "Not connected"}
                        </Badge>
                        <Link
                          as={RouterLink}
                          fontSize="sm"
                          to={GlobalSettingsUrl(env, "org-integrations")}
                        >
                          Manage integration
                        </Link>
                      </HStack>
                    </Flex>

                    {!org.integrations.slack ? (
                      <Text color="dimmed" fontSize="sm">
                        Connect Slack in Organization integrations to send
                        notifications.
                      </Text>
                    ) : (
                      <>
                        <VStack align="flex-start" spacing={1} w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Notification channel
                          </Text>
                          <Text color="dimmed" fontSize="sm" maxW="2xl">
                            Flag lifecycle and targeting notifications are sent
                            to this channel unless a specific flag overrides it.
                            Feedback notifications can use a separate channel in
                            the Feedback settings page.
                          </Text>
                          <SlackChannelAutocompleteSelect
                            canClear
                            isDisabled={isSavingSlack}
                            maxW="sm"
                            showDefaultChannel={false}
                            value={slackChannel}
                            onChange={(channel) => setSlackChannel(channel)}
                          />
                        </VStack>

                        <VStack align="flex-start" spacing={1} w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Targeting update policy
                          </Text>
                          <Text color="dimmed" fontSize="sm" maxW="2xl">
                            Choose which targeting changes send Slack
                            notifications. These policies set the default when
                            updating targeting, but you can always override
                            notifications for an individual targeting change.
                          </Text>
                          <SimpleSelect
                            isDisabled={isSavingSlack}
                            maxW="sm"
                            options={policyOptions}
                            value={slackPolicy}
                            onChange={setSlackPolicy}
                          />
                        </VStack>

                        {slackPolicy === "ALL_CHANGES" ? (
                          <VStack align="flex-start" spacing={2} w="full">
                            <Text color="dimmed" fontSize="sm" maxW="2xl">
                              Choose which environments trigger a Slack
                              notification for targeting changes.
                            </Text>
                            <EnvironmentSelector
                              addLabel="Add environment"
                              environments={environments}
                              isDisabled={isSavingSlack}
                              selectedEnvironmentIds={
                                slackSelectedEnvironmentIds
                              }
                              onChange={setSlackSelectedEnvironmentIds}
                            />
                          </VStack>
                        ) : null}

                        <Button
                          isLoading={isSavingSlack}
                          isDisabled={isSavingLinear}
                          onClick={() => void handleSaveSlack()}
                        >
                          Save Slack settings
                        </Button>
                      </>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              <Card w="full">
                <CardBody w="full">
                  <VStack align="flex-start" spacing={5} w="full">
                    <Flex align="center" justify="space-between" w="full">
                      <HStack>
                        <LinearConnectionIcon />
                        <Heading fontWeight="medium" size="sm">
                          Linear
                        </Heading>
                      </HStack>
                      <HStack spacing={3}>
                        <Badge
                          colorScheme={
                            org.integrations.linear ? "green" : "gray"
                          }
                        >
                          {org.integrations.linear
                            ? "Connected"
                            : "Not connected"}
                        </Badge>
                        <Link
                          as={RouterLink}
                          fontSize="sm"
                          to={GlobalSettingsUrl(env, "org-integrations")}
                        >
                          Manage integration
                        </Link>
                      </HStack>
                    </Flex>

                    {!org.integrations.linear ? (
                      <Text color="dimmed" fontSize="sm">
                        Connect Linear in Organization integrations to post
                        notification comments.
                      </Text>
                    ) : (
                      <>
                        <VStack align="flex-start" spacing={1} w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Targeting update policy
                          </Text>
                          <Text color="dimmed" fontSize="sm" maxW="2xl">
                            Choose which targeting changes post a comment on the
                            issue or project attached to each flag in Linear.
                            These policies set the default when updating
                            targeting, but you can always override notifications
                            for an individual targeting change.
                          </Text>
                          <SimpleSelect
                            isDisabled={isSavingLinear}
                            maxW="sm"
                            options={policyOptions}
                            value={linearPolicy}
                            onChange={setLinearPolicy}
                          />
                        </VStack>

                        {linearPolicy === "ALL_CHANGES" ? (
                          <VStack align="flex-start" spacing={2} w="full">
                            <Text color="dimmed" fontSize="sm" maxW="2xl">
                              Choose which environments trigger a Linear
                              notification for targeting changes.
                            </Text>
                            <EnvironmentSelector
                              addLabel="Add environment"
                              environments={environments}
                              isDisabled={isSavingLinear}
                              selectedEnvironmentIds={
                                linearSelectedEnvironmentIds
                              }
                              onChange={setLinearSelectedEnvironmentIds}
                            />
                          </VStack>
                        ) : null}

                        <Button
                          isLoading={isSavingLinear}
                          isDisabled={isSavingSlack}
                          onClick={() => void handleSaveLinear()}
                        >
                          Save Linear settings
                        </Button>
                      </>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {hasPoliciesError && (
                <Text color="dimmed" fontSize="sm">
                  Some notification values could not be loaded. Saving will use
                  defaults for missing values.
                </Text>
              )}
            </VStack>
          )}
        </VStack>
      </Restricted>
    </Flex>
  );
}
